import React, { useEffect, useCallback, useState, memo } from 'react'

import { Play } from '@guidde-co/shared.video-player.controls.play'
import { Time } from '@guidde-co/shared.video-player.controls.time'
import { Volume } from '@guidde-co/shared.video-player.controls.volume'
import { CurrentChapter } from '@guidde-co/shared.video-player.controls.current-chapter'
import { VideoSpeed } from '@guidde-co/shared.video-player.controls.video-speed'
import { Captions } from '@guidde-co/shared.video-player.controls.captions'
import { FullScreen } from '@guidde-co/shared.video-player.controls.full-screen'

import { SectionType } from '@guidde-co/shared.video-player.timeline-with-sections'
import { SpacedGroup } from '@guidde-co/shared.spaced-group'
import { useKeyDown } from '@guidde-co/shared.hooks.use-key-down'

import Box from '@material-ui/core/Box'
import makeStyles from '@material-ui/core/styles/makeStyles'

const useStyles = makeStyles(theme => ({
    timeControls: {
        padding: theme.spacing(0.8, 1),
        color: 'white',
        height: '52px',
        background:
            'linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), #090C10',
        zIndex: 10
    },
    icon: {
        cursor: 'pointer',
        color: 'white',
        fontSize: '20px'
    }
}))

const SKIP_TIME = {
    ON_CLICK: 1,
    ON_KEYPRESS: 5
}

export type CustomControlsProps = {
    onCaptionsClick?: () => void
    /**
     * Initial state of caption button
     */
    captionsInitialState?: boolean
    /**
     * do not render captions button
     */
    hideCaptions?: boolean
    /**
     * show company logo
     */
    showLogo?: boolean
    /**
     * allows jumping +5/-5 seconds on arrow click if hovered
     */
    isFocused: boolean
    /**
     * HTML video element
     */
    videoElement: HTMLVideoElement | null
    /**
     * Array<{ start: number, end: number }>
     */
    chapters: Array<SectionType>
    /**
     * do not render volume control
     */
    hideVolume?: boolean
    /**
     * do not render full screen control
     */
    hideFullScreen?: boolean
    /**
     * do not render current chapters control
     */
    hideCurrentChapters?: boolean
}

export const CustomControls = memo(
    ({
        showLogo,
        isFocused,
        onCaptionsClick,
        videoElement,
        chapters,
        hideFullScreen,
        hideCaptions,
        captionsInitialState,
        hideVolume,
         hideCurrentChapters
    }: CustomControlsProps) => {
        const classes = useStyles()

        const [duration, setDuration] = useState(videoElement?.duration || 0)
        const [currentTime, setCurrentTime] = useState(
            videoElement?.currentTime || 0
        )

        const updateTimeBySeconds = useCallback(
            (seconds: number) => {
                if (videoElement) {
                    return (videoElement.currentTime = seconds)
                }
            },
            [videoElement]
        )

        // Jump back on 5 seconds on left arrow click
        useKeyDown(
            37,
            () => updateTimeBySeconds(currentTime - SKIP_TIME.ON_KEYPRESS),
            isFocused
        )

        // Jump forward on 5 seconds on right arrow click
        useKeyDown(
            39,
            () => updateTimeBySeconds(currentTime + SKIP_TIME.ON_KEYPRESS),
            isFocused
        )

        useEffect(() => {
            if (!videoElement) return

            videoElement.addEventListener('loadeddata', (e: any) =>
                setDuration(e.target.duration)
            )
            videoElement.addEventListener('timeupdate', (e: any) =>
                setCurrentTime(e.target.currentTime)
            )

            return () => {
                videoElement.removeEventListener('durationchange', (e: any) =>
                    setDuration(e.target.duration)
                )
                videoElement.removeEventListener('timeupdate', (e: any) =>
                    setCurrentTime(e.target.currentTime)
                )
            }
        }, [videoElement])

        if (!videoElement) return null

        return (
            <Box
                className={classes.timeControls}
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
            >
                <Box display="flex" alignItems="center">
                    <Play videoElement={videoElement} />
                    <Time currentTime={currentTime} duration={duration} />
                    {!hideVolume && <Volume videoElement={videoElement} />}
                    <Box ml={1}>
                        {!hideCurrentChapters && (
                            <CurrentChapter
                                chapters={chapters}
                                currentTime={currentTime}
                                onUpdateTime={updateTimeBySeconds}
                            />
                        )}
                    </Box>
                </Box>

                <SpacedGroup spacing={2}>
                    {showLogo && <GuiddeLogo />}
                    <VideoSpeed videoElement={videoElement} />
                    {!hideCaptions && (
                        <Captions
                            videoElement={videoElement}
                            onClick={onCaptionsClick}
                            initialState={captionsInitialState}
                        />
                    )}
                    {!hideFullScreen && <FullScreen element={videoElement} />}
                </SpacedGroup>
            </Box>
        )
    }
)

const GuiddeLogo = () => {
    return (
        <svg
            width="46"
            height="14"
            viewBox="0 0 46 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M34.4932 0.93666H35.2924V0.933594C35.3496 0.933594 35.4044 0.956204 35.4448 0.99645C35.4852 1.0367 35.5079 1.09128 35.5079 1.1482V9.98781C35.5076 10.1061 35.4112 10.2019 35.2924 10.2019H34.4932C34.3744 10.2019 34.278 10.1061 34.2777 9.98781V9.32356C33.6529 9.98565 32.7831 10.3638 31.8708 10.37C29.941 10.37 28.3097 8.73238 28.3097 6.79329C28.3119 4.83522 29.905 3.24845 31.8708 3.2462C32.7786 3.24974 33.6465 3.61823 34.2777 4.26812V1.15126C34.2777 1.03274 34.3742 0.93666 34.4932 0.93666ZM29.5844 6.79584C29.5844 8.10083 30.5981 9.16312 31.8416 9.16312C33.1066 9.16312 34.2182 8.06558 34.2182 6.79891C34.2182 5.53224 33.1297 4.46229 31.8416 4.46229C30.576 4.46229 29.5844 5.49085 29.5844 6.79584Z"
                fill="white"
            />
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M26.7194 0.936659H25.9197C25.8007 0.936659 25.7042 1.03274 25.7042 1.15126V4.26812C25.0732 3.61826 24.2054 3.24976 23.2978 3.2462C21.33 3.24761 19.7354 4.8368 19.7351 6.79686C19.7351 8.73596 21.3659 10.3736 23.2962 10.3736C24.2083 10.3673 25.078 9.9891 25.7027 9.32714V9.99139C25.7029 10.1097 25.7993 10.2055 25.9181 10.2055H26.7179C26.8365 10.2052 26.9325 10.1095 26.9328 9.99139V1.15177C26.9328 1.03345 26.8367 0.937452 26.7179 0.93717L26.7194 0.936659ZM23.2675 9.16312C22.024 9.16312 21.0104 8.10083 21.0104 6.79584C21.0104 5.49085 22.001 4.45922 23.2675 4.45922C24.5556 4.45922 25.6437 5.52917 25.6437 6.79584C25.6437 8.06251 24.5336 9.16312 23.2675 9.16312Z"
                fill="white"
            />
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.99765 3.35555H6.15276C6.09566 3.35554 6.04091 3.37817 6.00059 3.41843C5.96027 3.45869 5.93768 3.51328 5.93782 3.57015V4.19863C5.31222 3.56029 4.45672 3.19704 3.56115 3.18948C1.63078 3.18948 0 4.82762 0 6.7662C0.00226153 8.72427 1.59532 10.311 3.56115 10.3133C4.4514 10.3085 5.30416 9.95565 5.93576 9.33071V9.4978C5.93576 11.2683 5.16629 12.166 3.6499 12.166C3.29051 12.1665 2.93654 12.0788 2.61931 11.9106C2.52732 11.862 2.41364 11.8861 2.34948 11.9678L1.84572 12.6106C1.8071 12.6597 1.79186 12.7232 1.80397 12.7844C1.81609 12.8456 1.85436 12.8985 1.90882 12.9294C2.44123 13.2245 3.04061 13.379 3.6499 13.378C4.66971 13.378 5.53871 13.0536 6.16353 12.4399C6.84939 11.7649 7.21207 10.7481 7.21207 9.49933V3.57219C7.21207 3.45367 7.11561 3.35759 6.99662 3.35759L6.99765 3.35555ZM3.53088 9.10334C2.26637 9.10334 1.27375 8.07733 1.27375 6.76672C1.27375 5.4561 2.28535 4.39995 3.53088 4.39995C4.79694 4.39995 5.90704 5.50567 5.90704 6.76672C5.90704 8.02777 4.81899 9.10334 3.53088 9.10334Z"
                fill="white"
            />
            <path
                d="M14.0143 3.35555H14.8135C14.9325 3.35555 15.029 3.45163 15.029 3.57015V9.99547C15.029 10.114 14.9325 10.2101 14.8135 10.2101H14.0143C13.8953 10.2101 13.7988 10.114 13.7988 9.99547V9.56065C13.2509 10.0558 12.5394 10.3158 11.7253 10.3158C9.73905 10.3158 8.89108 9.39612 8.89108 7.25008V3.57117C8.89108 3.45265 8.98754 3.35657 9.10653 3.35657H9.90628C10.0253 3.35657 10.1217 3.45265 10.1217 3.57117V7.25008C10.1217 8.48048 10.6609 9.10385 11.7253 9.10385C13.0627 9.10385 13.7988 8.35427 13.7988 6.99358V3.57015C13.7988 3.45163 13.8953 3.35555 14.0143 3.35555Z"
                fill="white"
            />
            <path
                d="M18.0099 3.47613H17.165C17.046 3.47613 16.9496 3.57221 16.9496 3.69073V10.1145C16.9496 10.1714 16.9723 10.2259 17.0127 10.2661C17.0531 10.3063 17.1079 10.3288 17.165 10.3286H18.0099C18.067 10.3288 18.1218 10.3063 18.1622 10.2661C18.2027 10.2259 18.2254 10.1714 18.2254 10.1145V3.69073C18.2254 3.57221 18.1289 3.47613 18.0099 3.47613Z"
                fill="white"
            />
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M40.4833 3.25182C39.5208 3.22569 38.5884 3.58699 37.8966 4.25409C37.2049 4.9212 36.8122 5.83782 36.8073 6.79686C36.8073 8.73596 38.4381 10.3736 40.3684 10.3736C41.6594 10.3775 42.8513 9.685 43.4833 8.56376C43.5212 8.49678 43.5202 8.41472 43.4807 8.34865C43.4424 8.28308 43.3717 8.24294 43.2955 8.24339H42.1639L42.1316 8.28427C41.7129 8.83415 41.0613 9.15892 40.3684 9.16312C39.3255 9.16312 38.414 8.41661 38.1544 7.37067H43.7141C43.8329 7.37067 43.9293 7.2749 43.9296 7.15658V6.86584C43.9296 4.93339 42.3834 3.3116 40.4833 3.2508V3.25182ZM38.1564 6.19188C38.4258 5.16588 39.3178 4.46024 40.3684 4.46024C41.4021 4.46024 42.3121 5.18069 42.5804 6.19188H38.1564Z"
                fill="white"
            />
            <path
                d="M18.053 2.49815H17.1219C17.0762 2.49829 17.0323 2.48025 16.9999 2.44802C16.9676 2.41579 16.9494 2.37205 16.9496 2.32647V1.39908C16.9496 1.30426 17.0268 1.2274 17.1219 1.2274H18.053C18.1481 1.22768 18.2251 1.30438 18.2254 1.39908V2.32647C18.2254 2.42129 18.1482 2.49815 18.053 2.49815Z"
                fill="white"
            />
            <path
                d="M44.5836 9.95153H45.5152C45.6101 9.95153 45.6871 9.8749 45.6871 9.78036V8.85297C45.6872 8.80748 45.6692 8.76381 45.6369 8.7316C45.6047 8.69939 45.5609 8.68128 45.5152 8.68128H44.5836C44.538 8.68128 44.4942 8.69939 44.4619 8.7316C44.4297 8.76381 44.4117 8.80748 44.4118 8.85297V9.78036C44.4118 9.8749 44.4887 9.95153 44.5836 9.95153Z"
                fill="white"
            />
        </svg>
    )
}
